import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Modal, message,Switch } from "antd";
import { useFormik } from "formik";
import * as Yup from "yup";
import { EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import Select from "react-select";
import { ClipLoader } from "react-spinners";
import { useDispatch, useSelector } from "react-redux";
import { fetchCoursesDropdownRequest } from "store/actions";

const CreateStudent = ({
  visible,
  handleClose,
  initialData = "",
  onSubmit,
  roles = [],
}) => {
  const dispatch = useDispatch();

  const [showPassword, setShowPassword] = useState(false);
  const [previewImage, setPreviewImage] = useState("");

  //  Get dropdown courses from Redux
  const dropdownCourses = useSelector(
    (state) => state.Course?.dropdownCourses?.data || []
  );
  const loading = useSelector((state) => state.Student.loading);

  //  Fetch dropdown courses when modal opens
  useEffect(() => {
    if (visible) dispatch(fetchCoursesDropdownRequest());
  }, [visible, dispatch]);

  //  Handle preview image loading
  useEffect(() => {
    if (initialData?.profile_image) {
      let imageSrc = "";
      if (typeof initialData.profile_image === "string") {
        imageSrc = initialData.profile_image;
      } else if (
        typeof initialData.profile_image === "object" &&
        initialData.profile_image.url
      ) {
        imageSrc = initialData.profile_image.url;
      } else if (
        typeof initialData.profile_image === "string" &&
        !initialData.profile_image.startsWith("http")
      ) {
        imageSrc = `${
          process.env.REACT_APP_API_URL || ""
        }/${initialData.profile_image}`;
      }
      setPreviewImage(imageSrc);
    } else {
      setPreviewImage("");
    }
  }, [initialData]);

  //  Formik setup
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: initialData?.name || "",
      email: initialData?.email || "",
      phone: initialData?.phone || "",
      password: "",
      courseId: initialData?.course?.id || "",
      mode: initialData?.mode || "",
      profile_image:
        typeof initialData?.profile_image === "string"
          ? initialData.profile_image
          : initialData?.profile_image?.url || "",
           status: typeof initialData?.status === "boolean" ? initialData.status : true,

    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      email: Yup.string()
        .required("Email is required")
        .matches(
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          "Invalid email address"
        ),
      phone: Yup.string()
        .required("Phone number is required")
        .matches(/^\d{10}$/, "Phone number must be exactly 10 digits"),
      password: initialData
        ? Yup.string()
        : Yup.string()
            .required("Password is required")
            .min(6, "Password must be at least 6 characters"),
      courseId: Yup.string().required("Course is required"),
      mode: Yup.string().required("Mode is required"),
    }),
    onSubmit: (values) => {
      const payload = {
        name: values.name,
        email: values.email,
        phone: values.phone,
        roleId: "68b563a9d7bc97f1ff404449",
        courseId: values.courseId,
        mode: values.mode,
        profile_image: values.profile_image || null,
        status: values.status,
      };

      if (!initialData) {
        payload.password = values.password;
        onSubmit(payload, onClose);
      } else {
        onSubmit(initialData?.id || initialData?._id, payload, onClose);
      }
    },
  });

  const onClose = () => {
    formik.resetForm();
    setPreviewImage("");
    handleClose();
  };

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handleProfileImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      message.error("Please upload a valid image");
      return;
    }
    const base64 = await getBase64(file);
    setPreviewImage(base64);
    formik.setFieldValue("profile_image", base64);
  };

  return (
    <Modal
      title={initialData ? "Edit Student" : "Create New Student"}
      open={visible}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      centered
      width={500}
      className="custom-modal-header p-0"
      maskClosable={false}
    >
      <form onSubmit={formik.handleSubmit}>
        <div className="row g-3">
          {/* Name */}
          <div className="col-12">
            <label className="modal-form">
              Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control fs-7"
              placeholder="Enter name"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
            />
            {formik.touched.name && formik.errors.name && (
              <span style={{ color: "red" }}>{formik.errors.name}</span>
            )}
          </div>

          {/* Email */}
          <div className="col-12">
            <label className="modal-form">
              Email <span className="text-danger">*</span>
            </label>
            <input
              type="email"
              className="form-control fs-7"
              placeholder="Enter email"
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
            />
            {formik.touched.email && formik.errors.email && (
              <span style={{ color: "red" }}>{formik.errors.email}</span>
            )}
          </div>

          {/* Phone */}
          <div className="col-12">
            <label className="modal-form">
              Phone <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control fs-7"
              placeholder="Enter phone number"
              name="phone"
              value={formik.values.phone}
              onChange={formik.handleChange}
            />
            {formik.touched.phone && formik.errors.phone && (
              <span style={{ color: "red" }}>{formik.errors.phone}</span>
            )}
          </div>

          {/* Password (only for Add) */}
          {!initialData && (
            <div className="col-12 position-relative">
              <label className="modal-form">
                Password <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <input
                  type={showPassword ? "text" : "password"}
                  className="form-control fs-7"
                  placeholder="Enter password"
                  name="password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                />
                <span
                  className="input-group-text"
                  style={{ cursor: "pointer" }}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                </span>
              </div>
              {formik.touched.password && formik.errors.password && (
                <span style={{ color: "red" }}>{formik.errors.password}</span>
              )}
            </div>
          )}

          {/*  Course */}
          <div className="col-12">
            <label className="modal-form">
              Course <span className="text-danger">*</span>
            </label>
            <Select
              placeholder="Select course"
              options={dropdownCourses.map((item) => ({
                label: item.title,
                value: item._id,
              }))}
              className="w-100"
              value={
                dropdownCourses
                  .map((item) => ({
                    label: item.title,
                    value: item._id,
                  }))
                  .find(
                    (opt) => opt.value === formik.values.courseId
                  ) || null
              }
              onChange={(option) =>
                formik.setFieldValue("courseId", option?.value || "")
              }
              isClearable
              isLoading={!dropdownCourses.length}
            />
            {formik.touched.courseId && formik.errors.courseId && (
              <span style={{ color: "red" }}>{formik.errors.courseId}</span>
            )}
          </div>

          {/* Mode */}
          <div className="col-12">
            <label className="modal-form">
              Mode <span className="text-danger">*</span>
            </label>
            <Select
              placeholder="Select mode"
              options={[
                { label: "Full Time", value: "full time" },
                { label: "Part Time", value: "part time" },
              ]}
              className="w-100"
              value={
                formik.values.mode
                  ? {
                      label:
                        formik.values.mode === "full time"
                          ? "Full Time"
                          : "Part Time",
                      value: formik.values.mode,
                    }
                  : null
              }
              onChange={(option) =>
                formik.setFieldValue("mode", option?.value || "")
              }
              isClearable
            />
            {formik.touched.mode && formik.errors.mode && (
              <span style={{ color: "red" }}>{formik.errors.mode}</span>
            )}
          </div>

          {/* Profile Image */}
          <div className="col-12">
            <label className="modal-form">Profile Image (optional)</label>
            <div className="form-control fs-7 p-2">
              <input
                type="file"
                accept="image/*"
                onChange={handleProfileImageChange}
                className="form-control border-0 p-0"
              />
            </div>
            {previewImage && (
              <div className="mt-2">
                <img
                  src={previewImage}
                  alt="Profile Preview"
                  style={{
                    width: "120px",
                    height: "80px",
                    objectFit: "cover",
                    borderRadius: "8px",
                    border: "1px solid #e0e0e0",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  }}
                />
              </div>
            )}
          </div>
                  {/* status */}
                        <div className="col-12 d-flex flex-column align-items-start justify-content-start">
              <label className="modal-form m-0">Status</label>
              <Switch
                checked={formik.values.status}
                onChange={(checked) => formik.setFieldValue('status', checked)}
                style={{
                  backgroundColor: formik.values.status ? 'black' : '#ccc',
                }}
              />
            </div>

        </div>

        {/* Footer */}
        <div className="modal-footer mt-3">
          <button
            type="button"
            className="btn btn-light btn-md"
            onClick={onClose}
          >
            Close
          </button>
          <button
            type="submit"
            className="btn btn-primary btn-md ms-3"
            style={{ minWidth: "80px" }}
            disabled={loading}
          >
            {loading ? (
              <ClipLoader size={16} color="white" />
            ) : initialData ? (
              "Update"
            ) : (
              "Add"
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

CreateStudent.propTypes = {
  visible: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  roles: PropTypes.array.isRequired,
};

export default CreateStudent;
